import React, { FC, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList as ReactWindowList } from 'react-window';
import { transparentize } from 'polished';
import { Dropdown, Portal, Loader, Button } from '@keen.io/ui-core';
import { BodyText } from '@keen.io/typography';
import { Icon } from '@keen.io/icons';
import { colors } from '@keen.io/colors';

import {
  Container,
  DropdownContainer,
  TitleContainer,
  FilterButtonSecondary,
  DropdownContent,
  LoaderContainer,
  DropdownFooter,
  EmptySearch,
  SelectedPropertiesNumber,
  DropdownHeader,
  IconWrapper,
  ListOverflow,
} from './FilterWidget.styles';

import { RootState } from '../../rootReducer';
import { getEventPath, getRelativeBoundingRect } from '../../utils';

import { FilterWidget as FilterWidgetType } from '../../modules/widgets/types';
import { DROPDOWN_CONTAINER_ID } from '../../constants';
import { FilterItem, SearchTags } from './components';
import { widgetsActions, widgetsSelectors } from '../../modules/widgets';

type Props = {
  /** Widget identifier */
  id: string;
  /** Disable chart interactions */
  disableInteractions?: boolean;
  /** Minimal dropdown width */
  minDropdownWidth?: number;
};

const Row = ({ data, index, style }) => {
  const { items, activeProperties, updateActiveProperties } = data;
  return (
    <div style={style} role="filter-item">
      <FilterItem
        key={items[index]}
        id={items[index]}
        isActive={activeProperties.includes(items[index])}
        label={items[index]}
        onChange={(e, isActive: boolean) => {
          e.preventDefault();
          updateActiveProperties(isActive, items[index]);
        }}
      />
    </div>
  );
};

const FilterWidget: FC<Props> = ({
  id,
  disableInteractions,
  minDropdownWidth = 220,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const widget = useSelector((state: RootState) =>
    widgetsSelectors.getWidget(state, id)
  );

  const filterWidget = widget.widget as FilterWidgetType;
  const [isOpen, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState({ x: 0, y: 0, width: 0 });
  const [searchMode, setSearchMode] = useState(false);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [activeProperties, setActiveProperties] = useState([]);
  const [containerOverflow, setContainerOverflow] = useState({
    top: false,
    bottom: false,
  });

  const containerRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const listRef = useRef(null);

  const { top: overflowTop, bottom: overflowBottom } = containerOverflow;

  const getDropdownXPositionThatFitsViewport = (left, right, dropdownWidth) => {
    if (window && window.innerWidth < left + dropdownWidth) {
      return right - dropdownWidth;
    }
    return left;
  };

  useEffect(() => {
    const outsideClick = (e) => {
      const path = getEventPath(e);
      if (
        !path?.includes(containerRef.current) &&
        !path?.includes(dropdownContainerRef.current)
      ) {
        setOpen(false);
      }
    };

    if (isOpen && containerRef.current) {
      const {
        left,
        bottom,
        right,
        width: parentWidth,
      } = getRelativeBoundingRect(DROPDOWN_CONTAINER_ID, containerRef.current);

      const dropdownWidth =
        parentWidth > minDropdownWidth ? parentWidth : minDropdownWidth;

      setDropdown((state) => ({
        ...state,
        x: getDropdownXPositionThatFitsViewport(left, right, minDropdownWidth),
        y: bottom,
        width: dropdownWidth,
      }));
    }

    document.addEventListener('click', outsideClick);
    return () => {
      document.removeEventListener('click', outsideClick);
    };
  }, [isOpen, containerRef]);

  useEffect(() => {
    if (isOpen && !widget.data) {
      dispatch(widgetsActions.setFilterWidget(widget.widget.id));
    }
    if (widget.data && widget.data.filter) {
      setActiveProperties(widget.data.filter.propertyValue);
    }
    if (!widget.data || !widget.data.filter) {
      setActiveProperties([]);
    }
  }, [isOpen]);

  const toggleSelectAll = () => {
    if (activeProperties.length === widget.data.propertyList.length) {
      return setActiveProperties([]);
    }
    return setActiveProperties(widget.data.propertyList);
  };

  const availableProperties = () => {
    if (widget.data && widget.data.propertyList) {
      return widget.data.propertyList.filter((property) =>
        property.toLowerCase().startsWith(searchPhrase.toLocaleLowerCase())
      );
    }
    return [];
  };

  const updateActiveProperties = (isActive, property) => {
    let properties = [...activeProperties];
    if (isActive) {
      properties.push(property);
    } else {
      properties = properties.filter((prop) => prop !== property);
    }
    setActiveProperties(properties);
  };

  const applyFilter = () => {
    if (activeProperties.length === 0) {
      dispatch(widgetsActions.unapplyFilterWidget(widget.widget.id));
    } else {
      dispatch(
        widgetsActions.applyFilterWidget({
          filterId: widget.widget.id,
          propertyValue: activeProperties,
        })
      );
      dispatch(widgetsActions.applyFilterModifiers(widget.widget.id));
    }
    setOpen(false);
  };

  const filterItemData = {
    items: availableProperties(),
    activeProperties: activeProperties,
    updateActiveProperties,
  };

  const onScroll = () => {
    if (!listRef.current) return;

    const {
      props: { height, itemCount, itemSize },
      state: { scrollOffset },
    } = listRef.current;

    const top = scrollOffset > 0;
    const bottom = itemSize * itemCount - height > scrollOffset;

    setContainerOverflow({ top, bottom });
  };

  return (
    <>
      <Container
        ref={containerRef}
        isOpen={isOpen}
        data-testid="filter-widget"
        onClick={() => {
          if (!disableInteractions) {
            setOpen(!isOpen);
          }
        }}
      >
        {
          <TitleContainer>
            <IconWrapper>
              <Icon
                type="funnel-widget-vertical"
                fill={transparentize(0.6, colors.blue[500])}
                width={13}
                height={13}
              />
            </IconWrapper>
            <BodyText
              variant="body2"
              fontWeight="bold"
              enableTextEllipsis
              role="heading"
            >
              {filterWidget.settings.name}
            </BodyText>
            {widget.isActive && (
              <SelectedPropertiesNumber data-testid="applied-properties-number">
                <BodyText
                  variant="body3"
                  fontWeight="bold"
                  color={colors.black[400]}
                  lineHeight={1}
                >
                  {widget.data.filter.propertyValue.length}
                </BodyText>
              </SelectedPropertiesNumber>
            )}
          </TitleContainer>
        }
      </Container>
      <Portal modalContainer={`#${DROPDOWN_CONTAINER_ID}`}>
        <DropdownContainer
          ref={dropdownContainerRef}
          customTransform={`translate(${dropdown.x}px, ${dropdown.y}px)`}
          width={dropdown.width}
        >
          <Dropdown isOpen={isOpen}>
            <DropdownHeader>
              {widget?.data?.propertyList && (
                <FilterButtonSecondary onClick={toggleSelectAll}>
                  {widget.data.propertyList.length === activeProperties.length
                    ? t('filter_widget.unselect_all')
                    : t('filter_widget.select_all')}
                </FilterButtonSecondary>
              )}
            </DropdownHeader>

            <DropdownContent>
              {(widget.isLoading ||
                (widget.data && widget.data.propertyList)) && (
                <SearchTags
                  isActive={searchMode}
                  searchPhrase={searchPhrase}
                  inputPlaceholder={t('filter_widget.search_value')}
                  searchLabel={t('filter_widget.search_value')}
                  onChangePhrase={(phrase) => setSearchPhrase(phrase)}
                  onClearPhrase={() => {
                    setSearchPhrase('');
                    setSearchMode(false);
                  }}
                  onActiveSearch={() => setSearchMode(true)}
                />
              )}
              {widget.isLoading ? (
                <LoaderContainer>
                  <Loader width={50} height={50} fill={colors.blue['500']} />
                </LoaderContainer>
              ) : availableProperties().length ? (
                <ListOverflow
                  overflowTop={overflowTop}
                  overflowBottom={overflowBottom}
                >
                  <ReactWindowList
                    height={150}
                    width="100%"
                    data-testid="scroll-wrapper2"
                    itemData={filterItemData}
                    itemCount={filterItemData.items.length}
                    itemSize={30}
                    ref={listRef}
                    onScroll={onScroll}
                    onItemsRendered={(e) => {
                      const { overscanStopIndex, visibleStopIndex } = e;

                      setContainerOverflow((state) => ({
                        ...state,
                        bottom: overscanStopIndex > visibleStopIndex,
                      }));
                    }}
                  >
                    {Row}
                  </ReactWindowList>
                </ListOverflow>
              ) : null}
              {!widget.isLoading && availableProperties().length === 0 && (
                <EmptySearch>
                  {t('filter_widget.no_properties_found')}
                </EmptySearch>
              )}
            </DropdownContent>
            {(widget.isLoading ||
              (widget.data && widget.data.propertyList)) && (
              <DropdownFooter enableBorder={!overflowBottom}>
                <Button size="small" onClick={applyFilter} variant="secondary">
                  {t('filter_widget.apply')}
                </Button>
                <FilterButtonSecondary
                  onClick={() => {
                    dispatch(
                      widgetsActions.unapplyFilterWidget(widget.widget.id)
                    );
                    setOpen(false);
                  }}
                >
                  {t('filter_widget.clear')}
                </FilterButtonSecondary>
              </DropdownFooter>
            )}
          </Dropdown>
        </DropdownContainer>
      </Portal>
    </>
  );
};

export default FilterWidget;
